import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { api, setAccountGoneHandler, setActiveAccount, setUnauthorizedHandler } from "../lib/api.js";
import { disablePush, syncPush } from "../lib/push.js";
import type { AccountInfo, MeResponse } from "../lib/types.js";

/**
 * "checking" is de korte periode waarin we bij de server navragen of het
 * sessiecookie nog geldig is. De app toont dan niets: een loginformulier dat na
 * een halve seconde weer verdwijnt is erger dan even wachten.
 */
type AuthStatus = "checking" | "anonymous" | "authenticated";

interface AuthValue {
  status: AuthStatus;
  /** Alle mailboxen die in deze browser aangemeld zijn; [0] is de standaard. */
  accounts: AccountInfo[];
  /** De mailbox waar de app nu naar kijkt. */
  active: AccountInfo | null;
  /** Of dit apparaat "aangemeld blijven" gebruikt; standaard voor een nieuw account. */
  remembered: boolean;
  /** Melding als een mailbox buiten de gebruiker om werd afgemeld. */
  notice: string | null;
  clearNotice: () => void;
  login: (address: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  addAccount: (address: string, password: string, remember: boolean) => Promise<void>;
  removeAccount: (id: string) => Promise<void>;
  switchAccount: (id: string) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Welke mailbox de app laatst toonde, per apparaat. Geen geheim (het is een
 * hash, geen adres) en alleen gemak: ontbreekt het, dan is het de eerste.
 */
const ACTIVE_KEY = "rw_active_account";

function rememberedActive(): string | null {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

function storeActive(id: string): void {
  try {
    localStorage.setItem(ACTIVE_KEY, id);
  } catch {
    /* privévenster of geblokkeerde opslag: dan is het de eerste */
  }
}

/**
 * Een tik op een pushmelding opent de app met ?account=<id>. Eenmalig
 * uitlezen en uit de adresbalk halen, zodat een herlaadactie later niet
 * opnieuw naar dat account springt.
 */
function takeAccountFromUrl(): string | null {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get("account");
    if (id === null) return null;
    url.searchParams.delete("account");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    return id;
  } catch {
    return null;
  }
}

function pick(accounts: AccountInfo[], ...preferred: (string | null)[]): AccountInfo | null {
  for (const id of preferred) {
    const hit = id === null ? undefined : accounts.find((a) => a.id === id);
    if (hit !== undefined) return hit;
  }
  return accounts[0] ?? null;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [accounts, setAccounts] = useState<AccountInfo[]>([]);
  const [active, setActive] = useState<AccountInfo | null>(null);
  const [remembered, setRemembered] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Voor callbacks die de actuele keuze nodig hebben zonder opnieuw aangemaakt
  // te worden bij elke wissel.
  const activeRef = useRef<AccountInfo | null>(null);

  /**
   * Eén plek waar het actieve account verandert. De API-laag krijgt het
   * METEEN, nog vóór React opnieuw rendert: de mailpagina van het nieuwe
   * account vuurt bij het mounten direct verzoeken af, en die moeten al de
   * goede account-header dragen.
   */
  const activate = useCallback((account: AccountInfo | null) => {
    activeRef.current = account;
    setActiveAccount(account?.id ?? null);
    if (account !== null) storeActive(account.id);
    setActive(account);
  }, []);

  /** Neemt een /me-antwoord over en kiest welk account er getoond wordt. */
  const adopt = useCallback(
    (me: MeResponse, ...preferred: (string | null)[]) => {
      setAccounts(me.accounts);
      setRemembered(me.remembered);
      activate(pick(me.accounts, ...preferred, activeRef.current?.id ?? null));
      setStatus("authenticated");
    },
    [activate],
  );

  const forget = useCallback(() => {
    activate(null);
    setAccounts([]);
    setStatus("anonymous");
  }, [activate]);

  const refreshAccounts = useCallback(async () => {
    try {
      adopt(await api.me());
    } catch {
      // Een 401 hier loopt via de unauthorized-handler naar het inlogscherm.
    }
  }, [adopt]);

  // Elke 401 uit welke aanroep dan ook betekent dat de sessie weg is; dan hoort
  // de hele app terug naar het loginscherm.
  useEffect(() => {
    setUnauthorizedHandler(forget);
    return () => {
      setUnauthorizedHandler(null);
    };
  }, [forget]);

  // Eén mailbox weg terwijl de rest nog aangemeld is: lijst bijwerken, en zo
  // nodig naar een account dat er nog wel is.
  useEffect(() => {
    setAccountGoneHandler((message) => {
      setNotice(message);
      void refreshAccounts().then(() => syncPush());
    });
    return () => {
      setAccountGoneHandler(null);
    };
  }, [refreshAccounts]);

  useEffect(() => {
    let cancelled = false;
    const fromUrl = takeAccountFromUrl();
    api
      .me(true)
      .then((value) => {
        if (cancelled) return;
        adopt(value, fromUrl, rememberedActive());
        // Herstelt ook een abonnement dat serverzijde zoekgeraakt is.
        void syncPush();
      })
      .catch(() => {
        if (!cancelled) forget();
      });
    return () => {
      cancelled = true;
    };
  }, [adopt, forget]);

  const login = useCallback(
    async (address: string, password: string, remember = false) => {
      const value = await api.login(address, password, remember);
      adopt(value, value.accounts[0]?.id ?? null);
      // Mailboxen die op dit apparaat onthouden waren komen bij het volgende
      // verzoek vanzelf terug; haal de volledige lijst meteen op.
      await refreshAccounts();
      void syncPush();
    },
    [adopt, refreshAccounts],
  );

  const logout = useCallback(async () => {
    try {
      // Uitloggen is ook: geen meldingen meer op dit apparaat. Eerst, want de
      // server wil voor het afmelden nog een geldige sessie zien.
      await disablePush().catch(() => undefined);
      await api.logout();
    } finally {
      // Ook als de server al niets meer van ons weet: lokaal is de sessie weg.
      forget();
    }
  }, [forget]);

  const addAccount = useCallback(
    async (address: string, password: string, remember: boolean) => {
      const value = await api.addAccount(address, password, remember);
      const added = value.accounts.find((a) => a.address === address.trim().toLowerCase());
      adopt(value, added?.id ?? null);
      void syncPush();
    },
    [adopt],
  );

  const removeAccount = useCallback(
    async (id: string) => {
      // De laatste mailbox afmelden is gewoon uitloggen, inclusief de meldingen.
      if (accounts.length <= 1) {
        await logout();
        return;
      }
      const value = await api.removeAccount(id);
      if (value === undefined) {
        forget();
        return;
      }
      adopt(value);
      // De server laat dit apparaat dan alleen nog meldingen krijgen voor de
      // mailboxen die er nog staan.
      void syncPush();
    },
    [accounts.length, adopt, forget, logout],
  );

  const switchAccount = useCallback(
    (id: string) => {
      const next = accounts.find((a) => a.id === id);
      if (next !== undefined && next.id !== activeRef.current?.id) activate(next);
    },
    [accounts, activate],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      accounts,
      active,
      remembered,
      notice,
      clearNotice,
      login,
      logout,
      addAccount,
      removeAccount,
      switchAccount,
    }),
    [status, accounts, active, remembered, notice, clearNotice, login, logout, addAccount, removeAccount, switchAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error("useAuth() buiten een AuthProvider gebruikt");
  return value;
}
